import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

const ChildContext = createContext();

export const useChild = () => {
  const context = useContext(ChildContext);
  if (!context) {
    throw new Error('useChild must be used within a ChildProvider');
  }
  return context;
};

export const ChildProvider = ({ children: childrenNodes }) => {
  const { user } = useAuth();
  const [selectedChild, _setSelectedChild] = useState(null);
  const [childList, setChildList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialSelectRef = useRef(false);

  // Per-user localStorage key — scoped so switching accounts never bleeds selection across users
  const storageKey = user?.uid ? `selectedChildId_${user.uid}` : null;

  // Wrapped setter: updates state and persists the selection to localStorage
  const setSelectedChild = (child) => {
    _setSelectedChild(child);
    if (!storageKey) return;
    if (child?.id) {
      localStorage.setItem(storageKey, child.id);
      // Cache gender for the pre-paint inline script in index.html that prevents
      // the pink/teal theme flash on hard refresh (same pattern as dark mode).
      if (child.gender) {
        localStorage.setItem('selectedChildGender', child.gender);
      } else {
        localStorage.removeItem('selectedChildGender');
      }
    } else {
      localStorage.removeItem(storageKey);
      localStorage.removeItem('selectedChildGender');
    }
  };

  // Real-time listeners: owned children, caregiver children, and journal-viewer children
  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // Local accumulator variables — updated by whichever listener fires
    let ownedList = [];
    let caregiverList = [];
    let viewerList = [];
    let ownedLoaded = false;
    let caregiverLoaded = false;
    let viewerLoaded = false;

    const merge = () => {
      // Owned/caregiver children take priority over viewer-only.
      // Deduplicate by id so a child owned AND shared never appears twice.
      const seen = new Set();
      const merged = [
        ...ownedList,
        ...caregiverList,
        ...viewerList.map((c) => ({ ...c, viewerOnly: true })),
      ].filter((child) => {
        if (seen.has(child.id)) return false;
        seen.add(child.id);
        return true;
      });
      setChildList(merged);

      // Only clear loading once all three queries have returned at least once
      if (ownedLoaded && caregiverLoaded && viewerLoaded) {
        setLoading(false);
        // Use a ref (not selectedChild) as the guard — the state value is a stale closure
        // here so !selectedChild would always be true on every Firestore snapshot re-fire.
        if (merged.length > 0 && !initialSelectRef.current) {
          initialSelectRef.current = true;
          const savedId = storageKey ? localStorage.getItem(storageKey) : null;
          const toSelect = (savedId && merged.find((c) => c.id === savedId)) || merged[0];
          _setSelectedChild(toSelect);
        }
      }
    };

    try {
      // Query 1: children this user owns
      const q1 = query(collection(db, 'children'), where('userId', '==', user.uid));
      const unsub1 = onSnapshot(q1, (snapshot) => {
        ownedList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        ownedLoaded = true;
        merge();
      }, (err) => {
        console.error('Error loading owned children:', err);
        ownedLoaded = true;
        if (caregiverLoaded && viewerLoaded) setLoading(false);
      });

      // Queries 2 & 3 only run when the user has an email
      if (user.email) {
        // Query 2: children where this user is listed as a caregiver
        const q2 = query(collection(db, 'children'), where('caregivers', 'array-contains', user.email));
        const unsub2 = onSnapshot(q2, (snapshot) => {
          caregiverList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          caregiverLoaded = true;
          merge();
        }, (err) => {
          console.error('Error loading caregiver children:', err);
          caregiverLoaded = true;
          if (ownedLoaded && viewerLoaded) setLoading(false);
        });

        // Query 3: children where this user has read-only journal viewer access
        const q3 = query(collection(db, 'children'), where('viewerEmails', 'array-contains', user.email));
        const unsub3 = onSnapshot(q3, (snapshot) => {
          viewerList = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          viewerLoaded = true;
          merge();
        }, (err) => {
          console.error('Error loading viewer children:', err);
          viewerLoaded = true;
          if (ownedLoaded && caregiverLoaded) setLoading(false);
        });

        return () => { unsub1(); unsub2(); unsub3(); initialSelectRef.current = false; };
      } else {
        // No email — skip email-based queries and mark them done immediately
        caregiverLoaded = true;
        viewerLoaded = true;
        return () => { unsub1(); initialSelectRef.current = false; };
      }
    } catch (err) {
      console.error('Error setting up children listeners:', err);
      setLoading(false);
    }
  // selectedChild intentionally excluded: we only want to auto-select the first
  // child on initial load, not re-run whenever the selection changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, user?.email]);

  const addChild = async (childData) => {
    if (!user?.uid) {
      throw new Error('User not authenticated');
    }
    try {
      const docRef = await addDoc(collection(db, 'children'), {
        ...childData,
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      return { id: docRef.id, ...childData, userId: user.uid };
    } catch (err) {
      console.error('Error adding child:', err);
      throw err;
    }
  };

  const updateChild = async (childId, updates) => {
    try {
      await updateDoc(doc(db, 'children', childId), {
        ...updates,
        updatedAt: new Date().toISOString(),
      });
      if (selectedChild?.id === childId) {
        setSelectedChild({ ...selectedChild, ...updates });
      }
    } catch (err) {
      console.error('Error updating child:', err);
      throw err;
    }
  };

  const deleteChild = async (childId) => {
    try {
      await deleteDoc(doc(db, 'children', childId));
      if (selectedChild?.id === childId && childList.length > 1) {
        const nextChild = childList.find((c) => c.id !== childId);
        setSelectedChild(nextChild);
      } else if (selectedChild?.id === childId) {
        setSelectedChild(null);
      }
    } catch (err) {
      console.error('Error deleting child:', err);
      throw err;
    }
  };

  const value = {
    selectedChild,
    setSelectedChild,
    childList,
    addChild,
    updateChild,
    deleteChild,
    loading,
  };

  return (
    <ChildContext.Provider value={value}>
      {childrenNodes}
    </ChildContext.Provider>
  );
};
